import { Component, effect, input } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'

import { ListItem } from '../../../../shared'
import { FluidFilterItem, MechanicInspection } from '../mechanic-inspection.model'

@Component({
    selector: 'app-mechanic-inspection-form',
    templateUrl: './mechanic-inspection-form.component.html',
    styleUrls: ['./mechanic-inspection-form.component.css'],
    standalone: true,
    imports: [FormsModule, WjInputModule],
})
export class MechanicInspectionFormComponent {
    inspection = input.required<MechanicInspection>()
    employeeOptions = input<ListItem<number>[]>([])
    equipmentOptions = input<ListItem<string>[]>([])

    verifyComplaintOptions: ListItem<string>[] = [
        new ListItem('', 'Select...'),
        new ListItem('Operator report reviewed', 'Operator report reviewed'),
        new ListItem('Complaint confirmed', 'Complaint confirmed'),
        new ListItem('Could NOT duplicate issue', 'Could NOT duplicate issue'),
    ]

    visualInspectionOptions: ListItem<string>[] = [
        new ListItem('Oil leak', 'Oil leak'),
        new ListItem('Fuel leak', 'Fuel leak'),
        new ListItem('Coolant leak', 'Coolant leak'),
        new ListItem('Hydraulic leak', 'Hydraulic leak'),
        new ListItem('Evidence of operator damage / abuse', 'Evidence of operator damage / abuse'),
        new ListItem('Damaged hoses / lines', 'Damaged hoses / lines'),
        new ListItem('Loose / missing bolts', 'Loose / missing bolts'),
        new ListItem('Cracked / broken components', 'Cracked / broken components'),
        new ListItem('Wiring damage / loose connectors', 'Wiring damage / loose connectors'),
        new ListItem('Signs of overheating', 'Signs of overheating'),
    ]

    engineSymptomOptions: ListItem<string>[] = [
        new ListItem('Starts normal', 'Starts normal'),
        new ListItem('Hard start', 'Hard start'),
        new ListItem('No start', 'No start'),
        new ListItem('Rough idle', 'Rough idle'),
        new ListItem('Low power', 'Low power'),
        new ListItem('Knocking / abnormal noise', 'Knocking / abnormal noise'),
        new ListItem('Excess smoke', 'Excess smoke'),
    ]

    smokeColorOptions: ListItem<string>[] = [
        new ListItem('', 'Select color...'),
        new ListItem('Black', 'Black'),
        new ListItem('White', 'White'),
        new ListItem('Blue', 'Blue'),
    ]

    engineBasicCheckOptions: ListItem<string>[] = [
        new ListItem('Battery voltage OK', 'Battery voltage OK'),
        new ListItem('Starter working', 'Starter working'),
        new ListItem('Fuel getting to engine', 'Fuel getting to engine'),
        new ListItem('Air intake clear', 'Air intake clear'),
    ]

    hydraulicSymptomOptions: ListItem<string>[] = [
        new ListItem('Functions operate normal', 'Functions operate normal'),
        new ListItem('Slow operation', 'Slow operation'),
        new ListItem('Weak power', 'Weak power'),
        new ListItem('No movement', 'No movement'),
        new ListItem('Jerky operation', 'Jerky operation'),
        new ListItem('System leak', 'System leak'),
    ]

    hydraulicCheckOptions: ListItem<string>[] = [
        new ListItem('Pump working', 'Pump working'),
        new ListItem('Cylinders moving correctly', 'Cylinders moving correctly'),
        new ListItem('Control valves responding', 'Control valves responding'),
    ]

    electricalCheckOptions: ListItem<string>[] = [
        new ListItem('Battery good', 'Battery good'),
        new ListItem('Charging system working', 'Charging system working'),
        new ListItem('Fuses / Relays OK', 'Fuses / Relays OK'),
        new ListItem('Wiring OK', 'Wiring OK'),
        new ListItem('Fault codes checked', 'Fault codes checked'),
    ]

    drivetrainOptions: ListItem<string>[] = [
        new ListItem('Machine moves normal', 'Machine moves normal'),
        new ListItem('No movement', 'No movement'),
        new ListItem('Slipping', 'Slipping'),
        new ListItem('Hard shifting', 'Hard shifting'),
        new ListItem('Steering issue', 'Steering issue'),
        new ListItem('Final drive noise', 'Final drive noise'),
    ]

    brakeOptions: ListItem<string>[] = [
        new ListItem('Brakes working', 'Brakes working'),
        new ListItem('Parking brake working', 'Parking brake working'),
        new ListItem('Weak braking', 'Weak braking'),
        new ListItem('Bad brakes', 'Bad brakes'),
    ]

    coolingOptions: ListItem<string>[] = [
        new ListItem('No issues', 'No issues'),
        new ListItem('Running hot', 'Running hot'),
        new ListItem('Coolant loss', 'Coolant loss'),
        new ListItem('Radiator plugged', 'Radiator plugged'),
        new ListItem('Fan working', 'Fan working'),
    ]

    attachmentOptions: ListItem<string>[] = [
        new ListItem('Operating normal', 'Operating normal'),
        new ListItem('Loose pins / bushings', 'Loose pins / bushings'),
        new ListItem('Cracked / damaged attachment', 'Cracked / damaged attachment'),
        new ListItem('Hydraulic issue at attachment', 'Hydraulic issue at attachment'),
    ]

    causeOptions: ListItem<string>[] = [
        new ListItem('', 'Select...'),
        new ListItem('Identified', 'Identified'),
        new ListItem('Not yet identified', 'Not yet identified'),
    ]

    statusOptions: ListItem<string>[] = [
        new ListItem('', 'Select...'),
        new ListItem('Fixed / returned to service', 'Fixed / returned to service'),
        new ListItem('Needs more work', 'Needs more work'),
        new ListItem('Needs to go to the shop', 'Needs to go to the shop'),
        new ListItem('Out of service', 'Out of service'),
    ]

    constructor() {
        effect(() => {
            const m = this.inspection()
            if (m.visualInspectionFindings.length) this.visualInspectionOptions = this.withChecked(this.visualInspectionOptions, m.visualInspectionFindings)
            if (m.engineSymptoms.length) this.engineSymptomOptions = this.withChecked(this.engineSymptomOptions, m.engineSymptoms)
            if (m.engineBasicChecks.length) this.engineBasicCheckOptions = this.withChecked(this.engineBasicCheckOptions, m.engineBasicChecks)
            if (m.hydraulicSymptoms.length) this.hydraulicSymptomOptions = this.withChecked(this.hydraulicSymptomOptions, m.hydraulicSymptoms)
            if (m.hydraulicChecks.length) this.hydraulicCheckOptions = this.withChecked(this.hydraulicCheckOptions, m.hydraulicChecks)
            if (m.electricalChecks.length) this.electricalCheckOptions = this.withChecked(this.electricalCheckOptions, m.electricalChecks)
            if (m.drivetrainSymptoms.length) this.drivetrainOptions = this.withChecked(this.drivetrainOptions, m.drivetrainSymptoms)
            if (m.brakeFindings.length) this.brakeOptions = this.withChecked(this.brakeOptions, m.brakeFindings)
            if (m.coolingFindings.length) this.coolingOptions = this.withChecked(this.coolingOptions, m.coolingFindings)
            if (m.attachmentFindings.length) this.attachmentOptions = this.withChecked(this.attachmentOptions, m.attachmentFindings)
        })
    }

    private withChecked(options: ListItem<string>[], selected: string[]): ListItem<string>[] {
        return options.map(opt => {
            const item = new ListItem(opt.value, opt.display)
            item.$checked = selected.includes(opt.value)
            return item
        })
    }

    multiSelectHeaderFormatter = (ms: any) => {
        const checked = ms.checkedItems
        if (!checked || checked.length === 0) return ''
        return checked.map((item: any) => item.display ?? item).join(', ')
    }

    private fluidOptionsCache = new Map<string, ListItem<string>[]>()

    getFluidStatusOptions(item: FluidFilterItem): ListItem<string>[] {
        let cached = this.fluidOptionsCache.get(item.label)
        if (!cached) {
            cached = [
                new ListItem('', 'Select...'),
                ...item.options.map(o => new ListItem(o, o)),
            ]
            this.fluidOptionsCache.set(item.label, cached)
        }
        return cached
    }

    onMultiSelectChanged(options: ListItem<string>[], target: string[]) {
        target.length = 0
        target.push(...options.filter(x => x.$checked).map(x => x.value))
    }

    onVisualInspectionChanged() {
        this.onMultiSelectChanged(this.visualInspectionOptions, this.inspection().visualInspectionFindings)
    }

    onEngineSymptomsChanged() {
        this.onMultiSelectChanged(this.engineSymptomOptions, this.inspection().engineSymptoms)
    }

    onEngineBasicChecksChanged() {
        this.onMultiSelectChanged(this.engineBasicCheckOptions, this.inspection().engineBasicChecks)
    }

    onHydraulicSymptomsChanged() {
        this.onMultiSelectChanged(this.hydraulicSymptomOptions, this.inspection().hydraulicSymptoms)
    }

    onHydraulicChecksChanged() {
        this.onMultiSelectChanged(this.hydraulicCheckOptions, this.inspection().hydraulicChecks)
    }

    onElectricalChecksChanged() {
        this.onMultiSelectChanged(this.electricalCheckOptions, this.inspection().electricalChecks)
    }

    onDrivetrainChanged() {
        this.onMultiSelectChanged(this.drivetrainOptions, this.inspection().drivetrainSymptoms)
    }

    onBrakeChanged() {
        this.onMultiSelectChanged(this.brakeOptions, this.inspection().brakeFindings)
    }

    onCoolingChanged() {
        this.onMultiSelectChanged(this.coolingOptions, this.inspection().coolingFindings)
    }

    onAttachmentChanged() {
        this.onMultiSelectChanged(this.attachmentOptions, this.inspection().attachmentFindings)
    }
}
